import React, { useState, useEffect, createContext } from 'react';
import { getLocale } from '@profile-ui/shared-components';
import { getI18nInstance } from '../helpers/i18nHelper';
import {
  fetchAemContent,
  fetchContentfulContent,
  parseContentfulData,
  buildContentfulUrl,
} from '../helpers';
import { FLAG_NAMES } from '../constants';
import { useLdFlags } from '../hooks';

const LocaleContext = createContext();

const LocaleProvider = ({ children, lang, skeletons = null, initialLocale = null }) => {
  const i18n = getI18nInstance();
  const [locale, setLocale] = useState(lang || initialLocale);
  const [isDataReady, setIsDataReady] = useState(false);
  const flags = useLdFlags();
  const contentfulEnabled = flags[FLAG_NAMES.UI_CONTENTFUL_ENABLED];

  useEffect(() => {
    if (lang && lang !== locale) {
      setLocale(lang);
      setIsDataReady(false);
    }
  }, [lang]);

  useEffect(() => {
    if (!locale) return;

    let cancelled = false;

    (async () => {
      const isoLocale = getLocale(locale);
      let parsedContent;
      
      if (contentfulEnabled) {
        const {
          CONTENTFUL_SPACE_ID,
          CONTENTFUL_ENVIRONMENT,
          CONTENTFUL_ENTRY_ID,
          CONTENTFUL_DELIVERY_TOKEN,
        } = window.$profileEnvVars;
        const contentfulFallbackUrl = `${window.location.origin}/static/contentfulFallback.json`;

        const contentfulUrl = buildContentfulUrl({
          spaceId: CONTENTFUL_SPACE_ID,
          environment: CONTENTFUL_ENVIRONMENT,
          entryId: CONTENTFUL_ENTRY_ID,
          locale: isoLocale,
          token: CONTENTFUL_DELIVERY_TOKEN,
        });

        const rawData = await fetchContentfulContent(contentfulUrl, isoLocale, contentfulFallbackUrl);
        parsedContent = parseContentfulData(rawData);
      } else {
        parsedContent = await fetchAemContent(isoLocale);
      }
      
      if (cancelled) return;

      for (let ns in parsedContent) {
        i18n.addResourceBundle(isoLocale, ns, parsedContent[ns]);
      }

      i18n.changeLanguage(isoLocale);
      setIsDataReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [locale, contentfulEnabled]);

  if (!isDataReady) return skeletons;

  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      {children}
    </LocaleContext.Provider>
  );
};

export { LocaleContext, LocaleProvider };
